import { Crown, Eye, Swords, Trophy, TrendingUp, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { gameToWatch, reigningChampion } from "@averagejoesff/shared";
import type { LeagueSnapshot, ManagerProfile, StandingRow } from "@averagejoesff/shared";
import { AppLink } from "../../components/AppLink";
import { formatRecord } from "../../lib/format";
import { HALL_OF_FAME_PATH, MANAGERS_PATH } from "../managers/paths";
import type { PeopleLookup } from "../managers/people";
import { ManagerLink } from "../managers/people";
import { ViewerBar } from "./ViewerBar";

/** Where a spectator can read more, as cards. The icons match the nav's. */
const EXPLORE_LINKS: { label: string; caption: string; href: string; icon: LucideIcon }[] = [
  { label: "Matchups", caption: "This week, previewed", href: "/matchups", icon: Swords },
  { label: "Hall of Fame", caption: "All-time records", href: HALL_OF_FAME_PATH, icon: Trophy },
  { label: "Managers", caption: "Every career", href: MANAGERS_PATH, icon: Users },
];

interface LeagueGlanceProps {
  snapshot: LeagueSnapshot;
  profiles: readonly ManagerProfile[];
  people: PeopleLookup;
  /** Back to "Who are you?". */
  onReset: () => void;
}

/**
 * What a spectator, who has no team of their own to follow, sees where a
 * manager sees their week: the league's headline facts and where to read more.
 * Each fact is one `Fact`, so a new one is one more row.
 */
export function LeagueGlance({ snapshot, profiles, people, onReset }: LeagueGlanceProps) {
  const champion = reigningChampion(profiles);
  const leader = snapshot.standings[0];
  const game = gameToWatch(snapshot);
  const standingOf = (personId: string) => snapshot.standings.find((row) => row.personId === personId);

  return (
    <>
      <ViewerBar actionLabel="Are you a manager?" onAction={onReset}>
        Watching as a spectator
      </ViewerBar>

      <ul className="home-facts">
        {champion && (
          <Fact icon={Crown} label="Reigning champion" meta={champion.season}>
            <ManagerLink personId={champion.personId} people={people} />
          </Fact>
        )}
        {leader && (
          <Fact icon={TrendingUp} label={snapshot.isFinished ? "Finished first" : "Leading the league"} meta={recordText(leader)}>
            <ManagerLink personId={leader.personId} people={people} />
          </Fact>
        )}
        {game && (
          <Fact icon={Eye} label="Game to watch">
            <GameSide personId={game.personIds[0]} people={people} standing={standingOf(game.personIds[0])} /> vs.{" "}
            <GameSide personId={game.personIds[1]} people={people} standing={standingOf(game.personIds[1])} />
          </Fact>
        )}
      </ul>

      <ul className="home-glance__links">
        {EXPLORE_LINKS.map(({ label, caption, href, icon: Icon }) => (
          <li key={href}>
            <AppLink href={href} className="home-glance__link">
              <Icon size={22} aria-hidden="true" />
              <span className="home-glance__label">{label}</span>
              <span className="home-glance__caption">{caption}</span>
            </AppLink>
          </li>
        ))}
      </ul>
    </>
  );
}

/** One headline fact: an icon, what it is, the answer, and an optional detail at the end of the row. */
function Fact({ icon: Icon, label, meta, children }: { icon: LucideIcon; label: string; meta?: string | number; children: ReactNode }) {
  return (
    <li className="home-fact">
      <span className="home-fact__icon">
        <Icon size={20} aria-hidden="true" />
      </span>
      <span className="home-fact__body">
        <span className="home-fact__label">{label}</span>
        <span className="home-fact__value">{children}</span>
      </span>
      {meta !== undefined && <span className="home-fact__meta">{meta}</span>}
    </li>
  );
}

const recordText = (row: StandingRow) => formatRecord(row.record.wins, row.record.losses, row.record.ties);

function GameSide({ personId, people, standing }: { personId: string; people: PeopleLookup; standing: StandingRow | undefined }) {
  return (
    <>
      <ManagerLink personId={personId} people={people} />
      {standing && <span className="home__when"> ({recordText(standing)})</span>}
    </>
  );
}
